import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select
} from '@mui/material'
import { FC, useCallback, useContext, useMemo } from 'react'

import {
  AnswerToCannedJobWithRelated,
  MultipleChoiceAnswerWithRelated
} from '../../../models/inspections'
import { ReplicacheContext } from '../../../providers/ReplicacheProvider/ReplicacheProvider'
import { updateInspectionCannedJobCache } from '../../../replicache/mutateFunction'
import {
  CannedJobExpanded,
  InspectionAnswerToCannedJobEntry
} from '../../../sdk'

interface Props {
  answers: MultipleChoiceAnswerWithRelated[]
  inspectionId: string
  open: boolean
  setOpen: (open: boolean) => void
}
type CannedJobWithAnswer = {
  cannedJob: CannedJobExpanded
  answerToCannedJob: InspectionAnswerToCannedJobEntry[]
}

function transformAnswerToCannedJobs(
  answerToCannedJobs: AnswerToCannedJobWithRelated[]
) {
  const res: { [key: string]: CannedJobWithAnswer } = {}
  for (const answerToCannedJob of answerToCannedJobs) {
    const { cannedJobExpanded, ...entry } = answerToCannedJob
    if (cannedJobExpanded.entry!.id! in res) {
      res[cannedJobExpanded.entry!.id!] = {
        cannedJob: cannedJobExpanded,
        answerToCannedJob: [
          ...res[cannedJobExpanded.entry!.id!].answerToCannedJob,
          { ...entry }
        ]
      }
    } else {
      res[cannedJobExpanded.entry!.id!] = {
        cannedJob: cannedJobExpanded,
        answerToCannedJob: [{ ...entry }]
      }
    }
  }
  return Object.values(res)
}

export const ActionSelectCached: FC<Props> = ({
  answers,
  inspectionId,
  open,
  setOpen
}) => {
  const { replicacheInstance } = useContext(ReplicacheContext)
  const cannedJobs = useMemo(
    () =>
      transformAnswerToCannedJobs(
        answers.flatMap((answer) => answer.answerToCannedJobs || [])
      ),
    [answers]
  )
  const selected = useMemo(
    () =>
      cannedJobs.filter((cannedJob) =>
        cannedJob.answerToCannedJob.some(
          (answerToCannedJob) => answerToCannedJob.info?.selected
        )
      ),
    [cannedJobs]
  )

  const handleChange = useCallback(
    (
      checked: boolean,
      answerToCannedJobs: InspectionAnswerToCannedJobEntry[]
    ) => {
      for (const answerToCannedJob of answerToCannedJobs) {
        const updated: InspectionAnswerToCannedJobEntry = {
          ...answerToCannedJob,
          info: {
            ...answerToCannedJob.info!,
            selected: checked
          }
        }
        updateInspectionCannedJobCache(
          inspectionId,
          updated,
          replicacheInstance
        )
      }
    },
    [inspectionId, replicacheInstance]
  )

  if (cannedJobs.length === 0) {
    return <></>
  }

  return (
    <FormControl variant="standard" sx={{ m: 1, width: 200 }}>
      <InputLabel id="multi-answer-label">Actions</InputLabel>
      <Select
        labelId="multi-answer-label"
        id="multi-answer"
        multiple
        value={selected.map((cannedJob) => cannedJob.cannedJob.entry!.id!)}
        renderValue={() =>
          selected
            .map((cannedJob) => cannedJob.cannedJob!.entry!.info!.name)
            .join(', ')
        }
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}>
        {cannedJobs.map((cannedJob) => (
          <MenuItem
            key={cannedJob.cannedJob.entry!.id!}
            value={cannedJob.cannedJob.entry!.id!}>
            <Checkbox
              checked={
                selected
                  .map((cannedJob) => cannedJob.cannedJob.entry!.id!)
                  .indexOf(cannedJob.cannedJob.entry!.id!) > -1
              }
              onChange={(event) =>
                handleChange(event.target.checked, cannedJob.answerToCannedJob)
              }
            />
            <ListItemText primary={cannedJob.cannedJob!.entry!.info!.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
